import React from 'react';
import { 
    FaPlus, 
    FaSearch, 
    FaExclamationTriangle, 
    FaFileAlt, 
    FaBuilding, 
    FaClipboardList,
    FaUsers,
    FaChartBar,
    FaCog,
    FaShieldAlt,
    FaTools
} from 'react-icons/fa';
import './EmptyState.css';

/**
 * Empty State Component
 * Displays helpful empty states with appropriate messaging and actions
 */
const EmptyState = ({ 
    type = 'default',
    title,
    description,
    actionLabel,
    onAction,
    illustration,
    size = 'medium',
    className = ''
}) => {
    // Predefined empty state configurations
    const emptyStateConfigs = {
        'no-properties': {
            icon: FaBuilding,
            title: 'No Properties Added Yet',
            description: 'Start by adding your first property to begin managing safety compliance.',
            actionLabel: 'Add Property',
            color: 'blue'
        },
        'no-assets': {
            icon: FaShieldAlt,
            title: 'No Assets Found',
            description: 'This property doesn\'t have any safety assets configured yet.',
            actionLabel: 'Add Assets',
            color: 'green'
        },
        'no-test-results': {
            icon: FaClipboardList,
            title: 'No Test Results',
            description: 'No testing data has been recorded for these assets yet.',
            actionLabel: 'View Testing Guide',
            color: 'orange'
        },
        'no-search-results': {
            icon: FaSearch,
            title: 'No Results Found',
            description: 'We couldn\'t find anything matching your search criteria. Try adjusting your filters.',
            actionLabel: 'Clear Filters',
            color: 'gray'
        },
        'no-staff': {
            icon: FaUsers,
            title: 'No Staff Members',
            description: 'Add staff members to manage property access and responsibilities.',
            actionLabel: 'Add Staff',
            color: 'purple'
        },
        'no-inspections': {
            icon: FaFileAlt,
            title: 'No Inspections Scheduled',
            description: 'Schedule regular inspections to maintain safety compliance.',
            actionLabel: 'Schedule Inspection',
            color: 'blue'
        },
        'no-reports': {
            icon: FaChartBar,
            title: 'No Reports Available',
            description: 'Generate reports once you have test data and inspection results.',
            actionLabel: 'Learn About Reports',
            color: 'indigo'
        },
        'maintenance-mode': {
            icon: FaTools,
            title: 'System Maintenance',
            description: 'This feature is temporarily unavailable due to system maintenance.',
            actionLabel: 'Check Status',
            color: 'yellow'
        },
        'access-denied': {
            icon: FaExclamationTriangle,
            title: 'Access Denied',
            description: 'You don\'t have permission to view this content. Contact your administrator.',
            actionLabel: 'Contact Support',
            color: 'red'
        },
        'error': {
            icon: FaExclamationTriangle,
            title: 'Something Went Wrong',
            description: 'An error occurred while loading this content. Please try again.',
            actionLabel: 'Retry',
            color: 'red'
        },
        'settings-empty': {
            icon: FaCog,
            title: 'No Settings Configured',
            description: 'Configure your system settings to customize your CloudSafe experience.',
            actionLabel: 'Open Settings',
            color: 'gray'
        }
    };

    const config = emptyStateConfigs[type] || {};
    const finalTitle = title || config.title || 'No Data Available';
    const finalDescription = description || config.description || 'There\'s nothing to show here right now.';
    const finalActionLabel = actionLabel || config.actionLabel;
    const IconComponent = config.icon || FaFileAlt;
    const color = config.color || 'gray';

    return (
        <div className={`empty-state empty-state-${size} empty-state-${color} ${className}`}>
            {illustration ? (
                <div className="empty-state-illustration">
                    {illustration}
                </div>
            ) : (
                <div className="empty-state-icon">
                    <IconComponent />
                </div>
            )}
            
            <div className="empty-state-content">
                <h3 className="empty-state-title">{finalTitle}</h3>
                <p className="empty-state-description">{finalDescription}</p>
                
                {(finalActionLabel && onAction) && (
                    <button 
                        className="empty-state-action"
                        onClick={onAction}
                    >
                        {finalActionLabel}
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * Empty State with Multiple Actions
 */
export const EmptyStateWithActions = ({ 
    type = 'default',
    title,
    description,
    actions = [],
    illustration,
    size = 'medium',
    className = ''
}) => {
    const emptyStateConfigs = {
        'getting-started': {
            icon: FaBuilding,
            title: 'Welcome to CloudSafe',
            description: 'Get started by setting up your first property and safety assets.',
            color: 'blue'
        }
    };

    const config = emptyStateConfigs[type] || {};
    const finalTitle = title || config.title || 'No Data Available';
    const finalDescription = description || config.description || 'There\'s nothing to show here right now.';
    const IconComponent = config.icon || FaFileAlt;
    const color = config.color || 'gray';

    return (
        <div className={`empty-state empty-state-${size} empty-state-${color} ${className}`}>
            {illustration ? (
                <div className="empty-state-illustration">
                    {illustration}
                </div>
            ) : (
                <div className="empty-state-icon">
                    <IconComponent />
                </div>
            )}
            
            <div className="empty-state-content">
                <h3 className="empty-state-title">{finalTitle}</h3>
                <p className="empty-state-description">{finalDescription}</p>
                
                {actions.length > 0 && (
                    <div className="empty-state-actions">
                        {actions.map((action, index) => (
                            <button 
                                key={index}
                                className={`empty-state-action ${action.variant || 'primary'}`}
                                onClick={action.onClick}
                            >
                                {action.icon && <action.icon />}
                                {action.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Empty State for Filtered Results
 */
export const FilteredEmptyState = ({ 
    searchTerm,
    filters = {},
    onClearFilters,
    onClearSearch,
    className = ''
}) => {
    const hasFilters = Object.values(filters).some(value => value && value !== '');
    
    return (
        <EmptyState
            type="no-search-results"
            title={searchTerm ? `No results for "${searchTerm}"` : 'No results found'}
            description={
                hasFilters 
                    ? 'Try adjusting your search term or filters to find what you\'re looking for.'
                    : 'Try a different search term or browse all items.'
            }
            actionLabel={hasFilters ? 'Clear Filters' : (searchTerm ? 'Clear Search' : null)}
            onAction={hasFilters ? onClearFilters : onClearSearch}
            className={className}
        />
    );
};

/**
 * Loading Empty State (for when data is being fetched)
 */
export const LoadingEmptyState = ({ 
    title = 'Loading...',
    description = 'Please wait while we fetch your data.',
    className = ''
}) => (
    <div className={`empty-state empty-state-medium empty-state-gray loading ${className}`}>
        <div className="empty-state-icon loading-icon">
            <div className="loading-spinner"></div>
        </div>
        <div className="empty-state-content">
            <h3 className="empty-state-title">{title}</h3>
            <p className="empty-state-description">{description}</p>
        </div>
    </div>
);

export default EmptyState;


